import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
    Grid,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import Speedometer from "react-d3-speedometer";
import Navbar from "../../AdminHeader/AdminHeader";
import { API_LoginURL } from "../../../../../config";
import SideBar from "../../AdminSidebar/AdminSidebar";
// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);

const SchoolSuccessFactor = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedYear, setSelectedYear] = useState(2025);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [schoolData, setSchoolData] = useState([]);
    const [schoolsList, setSchoolsList] = useState([]);
    const [filteredData, setFilteredData] = useState(null);
    
    // Debug state
    const [debugInfo, setDebugInfo] = useState("");
    
    // Get user role and admin's school from localStorage or context
    const userRole = localStorage.getItem("userRole") || "SuperAdmin"; // Change to "Admin" for admin view
    const adminSchool = localStorage.getItem("schoolName") || "KV";

    const headers = {
        headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
        },
    };

    // Chart options
    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: true,
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Average Score (%)'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Assessment Type'
                }
            }
        },
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: true,
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'SSF Score'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Year'
                }
            }
        },
    };

    // Years for dropdown
    const yearOptions = [2023, 2024, 2025, 2026,2027,2028];

    // Fetch school-wise SSPI data
    const fetchSchoolWiseData = async () => {
        setLoading(true);
        setError("");
        setDebugInfo("Fetching data...");
        
        try {
            const response = await axios.get(
                `${API_LoginURL}Sspi/SchoolWiseAverage`,
                headers
            );
            
            console.log("API Response:", response.data); // Debug log
            setDebugInfo(`API Response: ${JSON.stringify(response.data, null, 2)}`);
            
            if (response.data && response.data.message === "Success" && response.data.data) {
                setSchoolData(response.data.data);
                
                // Extract unique school names for dropdown
                const uniqueSchools = [...new Set(response.data.data.map(item => {
                    console.log("School item:", item); // Debug log
                    return item.schoolName;
                }))].filter(school => school && school.trim() !== "");
                
                console.log("Unique schools extracted:", uniqueSchools); // Debug log
                setSchoolsList(uniqueSchools);
                
                // For SuperAdmin, select first school by default
                // For Admin, set their specific school
                if (userRole === "SuperAdmin") {
                    if (uniqueSchools.length > 0) {
                        setSelectedSchool(uniqueSchools[0]);
                        setDebugInfo(`SuperAdmin mode: Selected ${uniqueSchools[0]}`);
                    } else {
                        setError("No schools found in data");
                        setDebugInfo("No schools found in data");
                    }
                } else {
                    setSelectedSchool(adminSchool);
                    setDebugInfo(`Admin mode: Selected ${adminSchool}`);
                }
                
            } else {
                setError("No data available or invalid response format");
                setDebugInfo("Invalid response format");
            }
        } catch (err) {
            console.error("Error fetching school data:", err);
            setError(`Failed to fetch school data: ${err.message}`);
            setDebugInfo(`Error: ${err.message}`);
            
            // For testing/demo purposes, use mock data if API fails
            const mockData = {
                message: "Success",
                data: [
                    {
                        schoolName: "KV",
                        subjectAverages: {
                            "Initial": 0,
                            "Half Year": 8.88,
                            "Final": 0
                        },
                        sspiYear: 2025,
                        overallSchoolAverage: 2.96,
                        message: "Success"
                    },
                    {
                        schoolName: "DPS",
                        subjectAverages: {
                            "Initial": 15.5,
                            "Half Year": 25.3,
                            "Final": 30.2
                        },
                        sspiYear: 2025,
                        overallSchoolAverage: 23.67,
                        message: "Success"
                    },
                    {
                        schoolName: "St. Mary's",
                        subjectAverages: {
                            "Initial": 20.1,
                            "Half Year": 35.4,
                            "Final": 40.8
                        },
                        sspiYear: 2025,
                        overallSchoolAverage: 32.1,
                        message: "Success"
                    }
                ]
            };
            
            console.log("Using mock data due to API error");
            setSchoolData(mockData.data);
            const mockSchools = ["KV", "DPS", "St. Mary's"];
            setSchoolsList(mockSchools);
            if (userRole === "SuperAdmin") {
                setSelectedSchool(mockSchools[0]);
            } else {
                setSelectedSchool(adminSchool);
            }
            setError("Using demo data (API failed)");
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on selected school and year
    useEffect(() => {
        console.log("Filtering data:", { selectedSchool, selectedYear, schoolData }); // Debug log
        
        if (schoolData.length > 0 && selectedSchool) {
            // Get all data for selected school
            const schoolFiltered = schoolData.filter(
                item => item.schoolName === selectedSchool
            );
            
            console.log("School filtered data:", schoolFiltered); // Debug log
            
            // Get data for selected year
            const yearFiltered = schoolFiltered.filter(
                item => item.sspiYear === selectedYear
            );
            
            // Get all years data for trend chart
            const yearlyData = schoolFiltered.sort((a, b) => a.sspiYear - b.sspiYear);
            
            console.log("Year filtered data:", yearFiltered); // Debug log
            console.log("Yearly trend data:", yearlyData); // Debug log
            
            setFilteredData({
                currentYear: yearFiltered[0] || null,
                yearlyTrend: yearlyData
            });
        } else {
            console.log("No data to filter"); // Debug log
        }
    }, [schoolData, selectedSchool, selectedYear]);

    // Initialize
    useEffect(() => {
        fetchSchoolWiseData();
    }, []);

    // Prepare bar chart data for subject averages
    const getSubjectBarData = () => {
        if (!filteredData?.currentYear?.subjectAverages) {
            return {
                labels: ['Initial', 'Half Year', 'Final'],
                datasets: [{
                    label: 'Average Scores',
                    data: [0, 0, 0],
                    backgroundColor: ['#FF8C00', '#3CB371', '#9370DB'],
                }]
            };
        }

        const { subjectAverages } = filteredData.currentYear;
        return {
            labels: Object.keys(subjectAverages),
            datasets: [{
                label: 'Average Scores (%)',
                data: Object.values(subjectAverages),
                backgroundColor: ['#FF8C00', '#3CB371', '#9370DB'],
                borderColor: ['#E67E00', '#2E8B57', '#8A2BE2'],
                borderWidth: 1
            }]
        };
    };

    // Prepare line chart data for SSPI trend
    const getSSPITrendData = () => {
        if (!filteredData?.yearlyTrend || filteredData.yearlyTrend.length === 0) {
            return {
                labels: yearOptions,
                datasets: [{
                    label: 'SSF Trend',
                    data: Array(yearOptions.length).fill(0),
                    borderColor: '#FF6347',
                    backgroundColor: 'rgba(255, 99, 71, 0.1)',
                    tension: 0.3,
                    fill: true
                }]
            };
        }

        // Create a map of year to SSPI
        const trendMap = {};
        filteredData.yearlyTrend.forEach(item => {
            trendMap[item.sspiYear] = item.overallSchoolAverage;
        });

        const labels = yearOptions;
        const data = yearOptions.map(year => trendMap[year] || 0);

        return {
            labels: labels,
            datasets: [{
                label: 'SSF Trend',
                data: data,
                borderColor: '#FF6347',
                backgroundColor: 'rgba(255, 99, 71, 0.1)',
                borderWidth: 3,
                tension: 0.3,
                fill: true,
                pointBackgroundColor: '#FF6347',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: 6
            }]
        };
    };

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SideBar/>
                <div className="main-panel">
                    <Box
                        sx={{
                            height: "calc(100vh - 60px)",
                            padding: "20px",
                            backgroundColor: "#F8F9FD",
                            overflowY: "auto",
                        }}
                    >
                        <Container maxWidth={false}>
                            {/* Page Header */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h4" gutterBottom>
                                    School Success Factor
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    {userRole === "SuperAdmin" 
                                        ? "Monitor and analyze school performance metrics across different schools" 
                                        : `Monitor ${adminSchool} performance metrics`}
                                </Typography>
                            </Box>

                            {/* Debug Info (remove in production) */}
                            {/* {process.env.NODE_ENV === 'development' && (
                                <Card sx={{ mb: 2, bgcolor: '#f0f0f0' }}>
                                    <CardContent>
                                        <Typography variant="caption" color="textSecondary">
                                            Debug Info:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            Role: {userRole}<br />
                                            Selected School: {selectedSchool}<br />
                                            Available Schools: {schoolsList.join(', ')}<br />
                                            Schools Count: {schoolsList.length}<br />
                                            {debugInfo}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )} */}

                            {/* Error Display */}
                            {error && (
                                <Alert severity={error.includes("demo") ? "info" : "error"} sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* Controls Card */}
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Grid container spacing={3} alignItems="center">
                                        {/* School Dropdown (only for SuperAdmin) */}
                                        {userRole === "SuperAdmin" && (
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="school-label">
                                                        {loading ? "Loading..." : "Select School"}
                                                    </InputLabel>
                                                    <Select
                                                        labelId="school-label"
                                                        value={selectedSchool}
                                                        label={loading ? "Loading..." : "Select School"}
                                                        onChange={(e) => setSelectedSchool(e.target.value)}
                                                        disabled={loading || schoolsList.length === 0}
                                                    >
                                                        {schoolsList.length === 0 ? (
                                                            <MenuItem disabled value="">
                                                                No schools available
                                                            </MenuItem>
                                                        ) : (
                                                            schoolsList.map((school) => (
                                                                <MenuItem key={school} value={school}>
                                                                    {school}
                                                                </MenuItem>
                                                            ))
                                                        )}
                                                    </Select>
                                                    {schoolsList.length === 0 && !loading && (
                                                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                                            No schools found in the data
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                        )}

                                        {/* Year Dropdown */}
                                        <Grid item xs={12} md={userRole === "SuperAdmin" ? 6 : 12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="year-label">Select Year</InputLabel>
                                                <Select
                                                    labelId="year-label"
                                                    value={selectedYear}
                                                    label="Select Year"
                                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                                    disabled={loading}
                                                >
                                                    {yearOptions.map((year) => (
                                                        <MenuItem key={year} value={year}>
                                                            {year}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Loading State */}
                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                                    <CircularProgress />
                                    <Typography sx={{ ml: 2 }}>Loading school data...</Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Current School Info Card */}
                                    {filteredData?.currentYear && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            School Name
                                                        </Typography>
                                                        <Typography variant="h6">
                                                            {filteredData.currentYear.schoolName}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Academic Year
                                                        </Typography>
                                                        <Typography variant="h6">
                                                            {filteredData.currentYear.sspiYear}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Overall School Success Rate
                                                        </Typography>
                                                        <Typography variant="h6" color="primary">
                                                            {filteredData.currentYear.overallSchoolAverage?.toFixed(2) || '0.00'}%
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Charts Container */}
                                    <Grid container spacing={3}>
                                        {/* Subject Averages Bar Chart */}
                                        <Grid item xs={12} lg={6}>
                                            <Card sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Subject Averages by Assessment Type ({selectedYear})
                                                    </Typography>
                                                    <Box sx={{ height: 300, mt: 2 }}>
                                                        <Bar 
                                                            data={getSubjectBarData()} 
                                                            options={barOptions}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* SSPI Speedometer */}
                                        <Grid item xs={12} lg={6}>
                                            <Card sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        School Success Score ({selectedYear})
                                                    </Typography>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'center', 
                                                        alignItems: 'center',
                                                        height: 300 
                                                    }}>
                                                        <Speedometer
                                                            value={filteredData?.currentYear?.overallSchoolAverage || 0}
                                                            minValue={0}
                                                            maxValue={100}
                                                            needleColor="#FF6347"
                                                            startColor="red"
                                                            endColor="green"
                                                            segments={10}
                                                            width={250}
                                                            height={200}
                                                            valueTextFontSize="20px"
                                                            currentValueText={`${(filteredData?.currentYear?.overallSchoolAverage || 0).toFixed(1)}%`}
                                                            segmentColors={[
                                                                "#FF0000", "#FF3300", "#FF6600", "#FF9900",
                                                                "#FFCC00", "#CCFF00", "#99FF00", "#66FF00",
                                                                "#33FF00", "#00FF00"
                                                            ]}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* SSPI Trend Line Chart */}
                                        <Grid item xs={12}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                    School Success Performance Trend ({selectedSchool})
                                                    </Typography>
                                                    <Box sx={{ height: 300, mt: 2 }}>
                                                        <Line 
                                                            data={getSSPITrendData()} 
                                                            options={lineOptions}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Data Table Summary */}
                                        {filteredData?.yearlyTrend && filteredData.yearlyTrend.length > 0 && (
                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Year-wise Performance Summary ({selectedSchool})
                                                        </Typography>
                                                        <Box sx={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#f5f7ff' }}>
                                                                        <th style={{ padding: '12px', textAlign: 'left' }}>Year</th>
                                                                        <th style={{ padding: '12px', textAlign: 'left' }}>Initial Assessment</th>
                                                                        <th style={{ padding: '12px', textAlign: 'left' }}>Half Yearly</th>
                                                                        <th style={{ padding: '12px', textAlign: 'left' }}>Final Exam</th>
                                                                        <th style={{ padding: '12px', textAlign: 'left' }}>Overall SSPI</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredData.yearlyTrend.map((item, index) => (
                                                                        <tr 
                                                                            key={index} 
                                                                            style={{ 
                                                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                                                                                borderBottom: '1px solid #e0e0e0'
                                                                            }}
                                                                        >
                                                                            <td style={{ padding: '12px' }}>{item.sspiYear}</td>
                                                                            <td style={{ padding: '12px' }}>
                                                                                {item.subjectAverages?.Initial?.toFixed(2) || '0.00'}%
                                                                            </td>
                                                                            <td style={{ padding: '12px' }}>
                                                                                {item.subjectAverages?.['Half Year']?.toFixed(2) || '0.00'}%
                                                                            </td>
                                                                            <td style={{ padding: '12px' }}>
                                                                                {item.subjectAverages?.Final?.toFixed(2) || '0.00'}%
                                                                            </td>
                                                                            <td style={{ padding: '12px', fontWeight: 'bold' }}>
                                                                                {item.overallSchoolAverage?.toFixed(2) || '0.00'}%
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Container>
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default SchoolSuccessFactor;